import type { FunctionComponent, HTMLAttributes, JSX } from "react";
import { useLayoutEffect, useRef } from "react";
import classnames from "classnames";
import { BetDetailsAction, BetDetailsColor } from "@ppb/the-wall-common/types";
import type { TagViewModel } from "@ppb/the-wall-common/types";
import { GenericIcon } from "@ppb/the-wall-icons/GenericIcon/GenericIcon";
import { SystemIconName, ValueIconName } from "@ppb/the-wall-icons";
import { Label } from "@ppb/the-wall-web";

import type { BetDetailsViewModel } from "./BetDetails.types";
import { SelectionHint } from "../SelectionHint/SelectionHint.web";
import styles from "./BetDetails.web.css";

// Local stand-in for the-wall's internal bricks/Tag/Tag, which isn't part of its public export surface.
const Tag: FunctionComponent<TagViewModel & HTMLAttributes<HTMLOrSVGElement>> = ({
  tagName: CustomTag,
  children,
  ...props
}) => <CustomTag {...props}>{children}</CustomTag>;

const actionIconColorTokenMap: { [key in BetDetailsColor]: { default: string; disabled: string } } = {
  [BetDetailsColor.Grey]: {
    default: "var(--bet-details-action-icon-primary-default-icon-colour)",
    disabled: "var(--bet-details-action-icon-primary-disabled-icon-colour)",
  },
  [BetDetailsColor.Teal]: {
    default: "var(--bet-selection-details-default-text-title-colour)",
    disabled: "var(--bet-details-action-icon-secondary-disabled-icon-colour)",
  },
  [BetDetailsColor.Black]: {
    default: "var(--bet-details-action-icon-secondary-default-icon-colour)",
    disabled: "var(--bet-details-action-icon-secondary-disabled-icon-colour)",
  },
};

const renderActionIcon = (action: BetDetailsAction, color: BetDetailsColor, isPlacing?: boolean): JSX.Element => {
  const iconColor = actionIconColorTokenMap[color][isPlacing ? "disabled" : "default"];

  const actionIconMap: { [key in BetDetailsAction]: JSX.Element } = {
    [BetDetailsAction.None]: <></>,
    [BetDetailsAction.Edit]: <GenericIcon name={SystemIconName.EDIT} color={iconColor} />,
    [BetDetailsAction.Remove]: <GenericIcon name={SystemIconName.CLOSE} color={iconColor} />,
  };

  return actionIconMap[action];
};

const SelectionTypeIcon: FunctionComponent<
  Pick<BetDetailsViewModel, "is90Min" | "selectionTypeIcon"> & { iconColorToken: string }
> = ({ is90Min, selectionTypeIcon, iconColorToken }) => {
  const resolvedIcon = is90Min ? ValueIconName.NINETY_MINUTE_PAYOUT_MONOCHROME : selectionTypeIcon;

  if (!resolvedIcon) return null;

  return (
    <div className={styles.selectionTypeIcon}>
      <GenericIcon name={resolvedIcon} color={iconColorToken} />
    </div>
  );
};

// This value maps to the duration of the CSS animation in BetDetails.web.css.
// If the animation duration is changed in CSS, this value should be updated to match it to ensure the highlight class is removed at the correct time.
const ANIMATION_DURATION = 550;

const animationClassMap = {
  [BetDetailsColor.Teal]: styles.titleSectionHighlightTealAnimation,
  [BetDetailsColor.Grey]: styles.titleSectionHighlightGreyAnimation,
  [BetDetailsColor.Black]: styles.titleSectionHighlightBlackAnimation,
};

export const BetDetails: FunctionComponent<BetDetailsViewModel> = ({
  tagName = "article",
  isDesktopLayout = false,
  title,
  subtitle,
  highlightedValueLabel,
  actionButtonLabel,
  action,
  onAction,
  color = BetDetailsColor.Teal,
  icon,
  isPlacing,
  selectionTypeIcon,
  is90Min,
  isGuaranteedPriceSelected,
  guaranteedPriceLabel,
  displayAllSubtitleText = false,
  hintMessage,
}) => {
  const labelRef = useRef<HTMLSpanElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevHighlightValue = useRef(highlightedValueLabel);

  useLayoutEffect(() => {
    if (prevHighlightValue.current === highlightedValueLabel) return;
    prevHighlightValue.current = highlightedValueLabel;

    // When already animating, ignore new animation triggers to prevent animation queue build-up
    if (timeoutRef.current || !labelRef.current) return;

    // Capture both the element and the animation class at the time of the effect run so that
    // cleanup always operates on the correct node and class, even if color or the ref changes.
    const labelEl = labelRef.current;
    const animationClass = animationClassMap[color];
    labelEl.classList.add(animationClass);

    timeoutRef.current = setTimeout(() => {
      labelEl.classList.remove(animationClass);
      timeoutRef.current = null;
    }, ANIMATION_DURATION);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      labelEl.classList.remove(animationClass);
    };
  }, [color, highlightedValueLabel]);

  const iconColorTokenMap: { [key in BetDetailsColor]: string } = {
    [BetDetailsColor.Teal]: "var(--bet-selection-details-default-text-title-colour)",
    [BetDetailsColor.Grey]: "var(--bet-details-neutral-icon-colour)",
    [BetDetailsColor.Black]: "var(--bet-details-boost-icon-colour)",
  };
  const iconColorToken = iconColorTokenMap[color];

  const classNames = classnames(styles.betDetails, {
    [styles.tealBg]: color === BetDetailsColor.Teal,
    [styles.greyBg]: color === BetDetailsColor.Grey,
    [styles.blackBg]: color === BetDetailsColor.Black,
  });

  const subtitleClassNames = classnames(styles.subtitle, {
    [styles.subtitleMultiline]: displayAllSubtitleText || isDesktopLayout,
    [styles.subtitleTeal]: color === BetDetailsColor.Teal,
    [styles.subtitleGrey]: color === BetDetailsColor.Grey,
    [styles.subtitleBlack]: color === BetDetailsColor.Black,
  });

  return (
    <Tag tagName={tagName} className={classNames}>
      {!!hintMessage && <SelectionHint hintMessage={hintMessage} />}
      <div className={styles.row}>
        {icon && <div className={styles.iconWrapper}>{icon}</div>}
        <div className={styles.content}>
          {typeof title === "string" ? (
            <span className={styles.title}>
              {title}
              {highlightedValueLabel && (
                <span ref={labelRef} className={styles.titleSectionHighlight}>
                  {` ${highlightedValueLabel}`}
                </span>
              )}
            </span>
          ) : (
            <>{title}</>
          )}
          {subtitle && <span className={subtitleClassNames}>{subtitle}</span>}
        </div>
        <div className={styles.statusIcons}>
          <SelectionTypeIcon is90Min={is90Min} selectionTypeIcon={selectionTypeIcon} iconColorToken={iconColorToken} />
          {isGuaranteedPriceSelected && guaranteedPriceLabel && (
            <div>
              <Label text={guaranteedPriceLabel} />
            </div>
          )}
        </div>
        {action !== BetDetailsAction.None && (
          <button
            className={styles.actionButton}
            type="button"
            disabled={isPlacing}
            onClick={onAction}
            aria-label={actionButtonLabel}
          >
            <div className={styles.actionIcon}>{renderActionIcon(action, color, isPlacing)}</div>
          </button>
        )}
      </div>
    </Tag>
  );
};
